import { SlideIcon } from "../components/Icons";
import type {
  GridConfig,
  MultipleConfig,
  GuessConfig,
  OpenConfig,
  QuizConfig,
  ScaleConfig,
  Slide,
  SlideConfig,
  WordcloudConfig,
} from "../lib/types";

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 10;

interface Props {
  slide: Slide;
  accent: string;
  onChange: (patch: { question?: string; config?: SlideConfig }) => void;
  onDelete: () => void;
  canDelete: boolean;
  fieldErrors: string[];
}

export function SlideEditor({
  slide,
  accent,
  onChange,
  onDelete,
  canDelete,
  fieldErrors,
}: Props) {
  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <SlideIcon type={slide.type} size={24} color={accent} />
        <span style={{ fontSize: 16, fontWeight: 600 }}>{slide.type_label}</span>
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-text btn-danger"
          onClick={onDelete}
          disabled={!canDelete}
          title={canDelete ? undefined : "A presentation needs at least one slide"}
        >
          Delete slide
        </button>
      </div>

      <div>
        <label className="label" htmlFor="question">
          Your question
        </label>
        <input
          id="question"
          className="input"
          style={{ fontSize: 16, fontWeight: 600, height: 54 }}
          placeholder="Type your question"
          value={slide.question}
          onChange={(e) => onChange({ question: e.target.value })}
        />
      </div>

      <TypeFields slide={slide} onChange={onChange} />

      {fieldErrors.length > 0 && (
        <p role="alert" style={{ color: "var(--error)", fontSize: 13 }}>
          {fieldErrors.join(" · ")}
        </p>
      )}
    </div>
  );
}

function TypeFields({ slide, onChange }: Pick<Props, "slide" | "onChange">) {
  const setConfig = (config: SlideConfig) => onChange({ config });

  switch (slide.type) {
    case "multiple":
    case "ranking":
    case "quiz": {
      const config = slide.config as QuizConfig;
      const isQuiz = slide.type === "quiz";
      const label =
        slide.type === "quiz"
          ? "Options — tick the correct one"
          : slide.type === "ranking"
            ? "Items to rank"
            : "Options";

      const multiCfg = slide.config as MultipleConfig;
      const isMultipleChoice = slide.type === "multiple";

      const setOptions = (options: string[], correct = config.correct) => {
        if (isQuiz) {
          setConfig({
            options,
            correct: Math.min(correct, options.length - 1),
            seconds: config.seconds,
          });
        } else if (isMultipleChoice) {
          const cap = multiCfg.max_choices;
          setConfig({
            options,
            multi: !!multiCfg.multi,
            // A cap larger than the list is rejected by the server.
            ...(multiCfg.multi && cap ? { max_choices: Math.min(cap, options.length) } : {}),
          });
        } else {
          setConfig({ options });
        }
      };

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span className="label" style={{ marginBottom: 0 }}>
            {label}
          </span>
          {config.options.map((option, index) => (
            // Index keys are right here: rows have no stable identity and
            // reordering happens by rewriting the whole array.
            <div key={index} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {isQuiz && (
                <button
                  onClick={() => setOptions(config.options, index)}
                  title="Mark as the correct answer"
                  aria-pressed={config.correct === index}
                  style={{
                    width: 28,
                    height: 28,
                    flex: "none",
                    borderRadius: "var(--radius-pill)",
                    border: `1.5px solid ${
                      config.correct === index ? "var(--success)" : "var(--outline)"
                    }`,
                    background: config.correct === index ? "#E3F0EC" : "var(--surface)",
                    color: config.correct === index ? "var(--success)" : "var(--text-tertiary)",
                    cursor: "pointer",
                    fontSize: 13,
                    lineHeight: 1,
                  }}
                >
                  ✓
                </button>
              )}
              <input
                className="input"
                aria-label={`Option ${index + 1}`}
                value={option}
                onChange={(e) => {
                  const next = [...config.options];
                  next[index] = e.target.value;
                  setOptions(next);
                }}
              />
              <button
                className="btn btn-text"
                aria-label={`Remove option ${index + 1}`}
                disabled={config.options.length <= MIN_OPTIONS}
                onClick={() => setOptions(config.options.filter((_, i) => i !== index))}
                style={{ flex: "none" }}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            className="btn btn-text"
            style={{ alignSelf: "flex-start" }}
            disabled={config.options.length >= MAX_OPTIONS}
            onClick={() => setOptions([...config.options, "New option"])}
          >
            + Add option
          </button>

          {isMultipleChoice && (
            <div
              style={{
                marginTop: 4,
                padding: 14,
                borderRadius: "var(--radius-control)",
                background: "var(--surface-container)",
              }}
            >
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={!!multiCfg.multi}
                  onChange={(e) =>
                    setConfig(
                      e.target.checked
                        ? { options: config.options, multi: true }
                        : { options: config.options, multi: false },
                    )
                  }
                  style={{ width: 17, height: 17, accentColor: "var(--primary)" }}
                />
                <span style={{ fontSize: 14, fontWeight: 600 }}>
                  Let people choose more than one
                </span>
              </label>
              {multiCfg.multi && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
                  <span className="tiny">Most they may pick</span>
                  <select
                    className="input"
                    style={{ width: 130, height: 38 }}
                    value={multiCfg.max_choices ?? ""}
                    onChange={(e) =>
                      setConfig({
                        options: config.options,
                        multi: true,
                        ...(e.target.value ? { max_choices: Number(e.target.value) } : {}),
                      })
                    }
                  >
                    <option value="">No limit</option>
                    {config.options.map((_, i) => (
                      <option key={i} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {isQuiz && (
            <Row>
              <Field label="Seconds to answer">
                <input
                  className="input"
                  type="number"
                  min={8}
                  max={60}
                  style={{ width: 120 }}
                  value={config.seconds}
                  onChange={(e) =>
                    setConfig({
                      options: config.options,
                      correct: config.correct,
                      seconds: Number(e.target.value),
                    })
                  }
                />
              </Field>
              <p className="muted" style={{ alignSelf: "flex-end", paddingBottom: 12 }}>
                Faster correct answers earn more points. A podium appears in the summary.
              </p>
            </Row>
          )}
        </div>
      );
    }

    case "scale": {
      const config = slide.config as ScaleConfig;
      return (
        <Row>
          <Field label="Low label (1)" grow>
            <input
              className="input"
              value={config.min_label}
              onChange={(e) => setConfig({ ...config, min_label: e.target.value })}
            />
          </Field>
          <Field label="High label (5)" grow>
            <input
              className="input"
              value={config.max_label}
              onChange={(e) => setConfig({ ...config, max_label: e.target.value })}
            />
          </Field>
        </Row>
      );
    }

    case "guess": {
      const config = slide.config as GuessConfig;
      const num = (key: keyof GuessConfig) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setConfig({ ...config, [key]: Number(e.target.value) });
      return (
        <>
          <Row>
            <Field label="Correct answer">
              <input
                className="input"
                type="number"
                style={{ width: 140 }}
                value={config.answer}
                onChange={num("answer")}
              />
            </Field>
            <Field label="Range from">
              <input
                className="input"
                type="number"
                style={{ width: 120 }}
                value={config.lo}
                onChange={num("lo")}
              />
            </Field>
            <Field label="to">
              <input
                className="input"
                type="number"
                style={{ width: 120 }}
                value={config.hi}
                onChange={num("hi")}
              />
            </Field>
          </Row>
          <p className="tiny">The answer has to sit inside the range.</p>
        </>
      );
    }

    case "grid": {
      const config = slide.config as GridConfig;
      const text = (key: keyof GridConfig) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setConfig({ ...config, [key]: e.target.value });
      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12,
          }}
        >
          <Field label="X axis · left">
            <input className="input" value={config.x_lo} onChange={text("x_lo")} />
          </Field>
          <Field label="X axis · right">
            <input className="input" value={config.x_hi} onChange={text("x_hi")} />
          </Field>
          <Field label="Y axis · bottom">
            <input className="input" value={config.y_lo} onChange={text("y_lo")} />
          </Field>
          <Field label="Y axis · top">
            <input className="input" value={config.y_hi} onChange={text("y_hi")} />
          </Field>
        </div>
      );
    }

    case "wordcloud": {
      const config = slide.config as WordcloudConfig;
      return (
        <Field label="Words each person may send">
          <input
            className="input"
            type="number"
            min={1}
            max={10}
            style={{ width: 120 }}
            value={config.max_words}
            onChange={(e) => setConfig({ max_words: Number(e.target.value) })}
          />
        </Field>
      );
    }

    case "open": {
      const config = slide.config as OpenConfig;
      return (
        <Field label="Maximum characters per answer">
          <input
            className="input"
            type="number"
            min={20}
            max={2000}
            style={{ width: 120 }}
            value={config.max_length}
            onChange={(e) => setConfig({ max_length: Number(e.target.value) })}
          />
        </Field>
      );
    }
  }
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>{children}</div>;
}

function Field({
  label,
  children,
  grow,
}: {
  label: string;
  children: React.ReactNode;
  grow?: boolean;
}) {
  return (
    <label style={{ display: "block", flex: grow ? "1 1 160px" : undefined }}>
      <span className="label">{label}</span>
      {children}
    </label>
  );
}
